import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaSave,
  FaTimes,
  FaPlus,
  FaTrash,
  FaUpload,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import OUpload from "./OUpload";
import { storytellersAPI } from "../services/api";
import "./StorytellerForm.scss";

const StorytellerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: "",
    location: "",
    specialty: "",
    story: "",
    description: "",
    image: "",
    languages: [],
    timeline: "",
    duration: "",
    minPeople: "",
    maxPeople: "",
    minAge: "",
    pricePerPerson: "",
    keyHighlights: [{ title: "", description: "" }],
    tourSchedule: [
      {
        day: "Day 1",
        title: "",
        theme: "",
        timeline: "",
        activities: [""],
        meals: [{ time: "", name: "" }],
      },
    ],
    inclusions: [""],
    exclusions: [""],
    gallery: [],
    status: "draft",
    is_featured: false,
    is_popular: false,
    sort_order: 0,
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    experience: true,
    highlights: true,
    schedule: true,
    inclusions: true,
    gallery: true,
    settings: true,
    seo: true,
  });

  const fetchStoryteller = useCallback(async () => {
    try {
      setLoading(true);
      const response = await storytellersAPI.getById(id);

      if (!response.success) {
        throw new Error(response.message || "Failed to fetch storyteller");
      }

      // Convert JSON strings back to arrays for form usage
      const processedData = { ...response.data };

      const arrayFields = [
        "languages",
        "keyHighlights",
        "tourSchedule",
        "inclusions",
        "exclusions",
        "gallery",
      ];

      arrayFields.forEach((field) => {
        if (processedData[field] && typeof processedData[field] === "string") {
          try {
            processedData[field] = JSON.parse(processedData[field]);
          } catch (error) {
            console.error(`Error parsing ${field}:`, error);
            processedData[field] = [];
          }
        } else if (!processedData[field]) {
          processedData[field] = [];
        }
      });

      setFormData(processedData);
      setDataLoaded(true);

      // Debug: Check what data we're getting
      console.log("Fetched storyteller data:", processedData);
      console.log("Story value:", processedData.story);
      console.log("Description value:", processedData.description);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Debug: Monitor formData changes
  useEffect(() => {
    console.log("FormData updated:", formData);
    console.log("Story in formData:", formData.story);
    console.log("Description in formData:", formData.description);
  }, [formData]);

  // Fetch storyteller data for editing
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isEdit) {
      fetchStoryteller();
    }
  }, [id, isEdit, fetchStoryteller]);

  // Helper functions for auto-generating SEO content
  const generateMetaTitle = (name, specialty, location) => {
    if (!name) return "";
    const baseTitle = `${name} - ${specialty || "Storyteller"}`;
    const locationText = location ? ` in ${location}` : "";
    return `${baseTitle}${locationText}`.substring(0, 60);
  };

  const generateMetaDescription = (name, specialty, location, story) => {
    if (!name) return "";
    const baseDesc = `Experience authentic storytelling with ${name}`;
    const specialtyText = specialty ? `, a ${specialty}` : "";
    const locationText = location ? ` in ${location}` : "";

    // Strip HTML tags from story for meta description
    const plainTextStory = story ? story.replace(/<[^>]*>/g, "") : "";
    const storyText = plainTextStory
      ? `. ${plainTextStory.substring(0, 100)}...`
      : "";

    const fullDesc = `${baseDesc}${specialtyText}${locationText}${storyText}`;
    return fullDesc.substring(0, 160);
  };

  const generateMetaKeywords = (specialty, location) => {
    const keywords = [];
    if (specialty) keywords.push(specialty.toLowerCase());
    if (location)
      keywords.push(location.toLowerCase(), `${location.toLowerCase()} tours`);
    keywords.push(
      "storytelling",
      "cultural experience",
      "authentic tours",
      "local guide"
    );
    return keywords.join(", ");
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // Auto-populate SEO settings based on basic information
      if (
        name === "name" ||
        name === "specialty" ||
        name === "location" ||
        name === "story"
      ) {
        // Only auto-populate if SEO fields are empty (first time) or user hasn't manually edited them
        const shouldAutoPopulate =
          !prev.meta_title ||
          prev.meta_title ===
            generateMetaTitle(prev.name, prev.specialty, prev.location);

        if (shouldAutoPopulate) {
          newData.meta_title = generateMetaTitle(
            newData.name,
            newData.specialty,
            newData.location
          );
          newData.meta_description = generateMetaDescription(
            newData.name,
            newData.specialty,
            newData.location,
            newData.story
          );
          newData.meta_keywords = generateMetaKeywords(
            newData.specialty,
            newData.location
          );
        }
      }

      return newData;
    });
  };

  // Handle textarea changes
  const handleTextareaChange = (field, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Auto-populate SEO settings when story changes
      if (field === "story") {
        const shouldAutoPopulate =
          !prev.meta_title ||
          prev.meta_title ===
            generateMetaTitle(prev.name, prev.specialty, prev.location);

        if (shouldAutoPopulate) {
          newData.meta_title = generateMetaTitle(
            newData.name,
            newData.specialty,
            newData.location
          );
          newData.meta_description = generateMetaDescription(
            newData.name,
            newData.specialty,
            newData.location,
            newData.story
          );
          newData.meta_keywords = generateMetaKeywords(
            newData.specialty,
            newData.location
          );
        }
      }

      return newData;
    });
  };

  const handleArrayChange = (field, index, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const handleAddArrayItem = (field, defaultValue = "") => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], defaultValue],
    }));
  };

  const handleRemoveArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleHighlightChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      keyHighlights: prev.keyHighlights.map((highlight, i) =>
        i === index ? { ...highlight, [field]: value } : highlight
      ),
    }));
  };

  const handleScheduleChange = (dayIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      tourSchedule: prev.tourSchedule.map((day, i) =>
        i === dayIndex ? { ...day, [field]: value } : day
      ),
    }));
  };

  const handleActivityChange = (dayIndex, activityIndex, value) => {
    setFormData((prev) => ({
      ...prev,
      tourSchedule: prev.tourSchedule.map((day, i) =>
        i === dayIndex
          ? {
              ...day,
              activities: day.activities.map((activity, j) =>
                j === activityIndex ? value : activity
              ),
            }
          : day
      ),
    }));
  };

  const handleMealChange = (dayIndex, mealIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      tourSchedule: prev.tourSchedule.map((day, i) =>
        i === dayIndex
          ? {
              ...day,
              meals: day.meals.map((meal, j) =>
                j === mealIndex ? { ...meal, [field]: value } : meal
              ),
            }
          : day
      ),
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Image upload handlers
  const handleMainImageUploaded = (image) => {
    setFormData((prev) => ({
      ...prev,
      image: image,
    }));
  };

  const handleGalleryImageUploaded = (images) => {
    setFormData((prev) => ({
      ...prev,
      gallery: images,
    }));
  };

  // Loader functions (you can replace these with your actual loader functions)
  const $startLoader = () => {
    // Add your loader start logic here
    console.log("Upload started...");
  };

  const $stopLoader = () => {
    // Add your loader stop logic here
    console.log("Upload completed...");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const response = isEdit
        ? await storytellersAPI.update(id, formData)
        : await storytellersAPI.create(formData);

      if (!response.success) {
        throw new Error(response.message || "Failed to save storyteller");
      }

      setSuccess(true);
      setTimeout(() => {
        navigate("/storytellers");
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !isEdit) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="storyteller-form">
      <div className="form-container">
        {/* Header */}
        <div className="form-header">
          <div className="header-content">
            <div className="header-text">
              <h1>{isEdit ? "Edit Storyteller" : "Add New Storyteller"}</h1>
              <p>
                {isEdit
                  ? "Update storyteller information and settings"
                  : "Create a new storyteller profile with all necessary details"}
              </p>
            </div>
            <button
              onClick={() => navigate("/storytellers")}
              className="cancel-button"
            >
              <FaTimes className="icon" />
              Cancel
            </button>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="alert success">
            <div className="alert-content">
              <p>Storyteller {isEdit ? "updated" : "created"} successfully!</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="alert error">
            <div className="alert-content">
              <p>{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="form-sections">
          {/* Basic Information */}
          <div className="form-section">
            <button
              type="button"
              onClick={() => toggleSection("basic")}
              className="section-header"
            >
              <div className="section-title">
                <h2>Basic Information</h2>
                <p>Essential details about the storyteller</p>
              </div>
              {expandedSections.basic ? (
                <FaChevronUp className="chevron-icon" />
              ) : (
                <FaChevronDown className="chevron-icon" />
              )}
            </button>

            {expandedSections.basic && (
              <div className="section-content">
                <div className="form-grid">
                  <div className="form-field">
                    <label>
                      Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter storyteller name"
                    />
                  </div>
                  <div className="form-field">
                    <label>
                      Location <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter location"
                    />
                  </div>
                  <div className="form-field">
                    <label>
                      Specialty <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="specialty"
                      value={formData.specialty}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter specialty"
                    />
                  </div>
                  <div className="form-field">
                    <label>
                      Profile Image <span className="required">*</span>
                    </label>
                    <OUpload
                      multiple={false}
                      value={formData.image}
                      onImagesUploaded={handleMainImageUploaded}
                      onStartLoader={$startLoader}
                      onStopLoader={$stopLoader}
                      text="Upload storyteller profile image"
                      size="md"
                      imagesBaseUrl="https://songstarktours.s3.ap-northeast-2.amazonaws.com"
                    />
                  </div>
                  <div className="form-field full-width">
                    <label>
                      Story <span className="required">*</span>
                    </label>
                    <textarea
                      value={formData.story || ""}
                      onChange={(e) =>
                        handleTextareaChange("story", e.target.value)
                      }
                      placeholder="Tell the storyteller's story..."
                      rows={15}
                      style={{ height: "400px", resize: "vertical" }}
                      className="form-textarea"
                    />
                    {/* Debug: Log current story value */}
                    {console.log("Textarea Story value:", formData.story)}
                  </div>
                  <div className="form-field full-width">
                    <label>
                      Description <span className="required">*</span>
                    </label>
                    <textarea
                      value={formData.description || ""}
                      onChange={(e) =>
                        handleTextareaChange("description", e.target.value)
                      }
                      placeholder="Detailed description of the storyteller..."
                      rows={15}
                      style={{ height: "400px", resize: "vertical" }}
                      className="form-textarea"
                    />
                    {/* Debug: Log current description value */}
                    {console.log(
                      "Textarea Description value:",
                      formData.description
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Experience Details */}
          <div className="form-section">
            <button
              type="button"
              onClick={() => toggleSection("experience")}
              className="section-header"
            >
              <div className="section-title">
                <h2>Experience Details</h2>
                <p>Tour specifications and requirements</p>
              </div>
              {expandedSections.experience ? (
                <FaChevronUp className="chevron-icon" />
              ) : (
                <FaChevronDown className="chevron-icon" />
              )}
            </button>

            {expandedSections.experience && (
              <div className="section-content">
                <div className="form-grid three-cols">
                  <div className="form-field">
                    <label>Timeline</label>
                    <input
                      type="text"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      placeholder="e.g., 3 days, 2 nights"
                    />
                  </div>
                  <div className="form-field">
                    <label>Duration</label>
                    <input
                      type="text"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      placeholder="e.g., 6 hours daily"
                    />
                  </div>
                  <div className="form-field">
                    <label>Min People</label>
                    <input
                      type="number"
                      name="minPeople"
                      value={formData.minPeople}
                      onChange={handleInputChange}
                      min="1"
                      placeholder="1"
                    />
                  </div>
                  <div className="form-field">
                    <label>Max People</label>
                    <input
                      type="number"
                      name="maxPeople"
                      value={formData.maxPeople}
                      onChange={handleInputChange}
                      min="1"
                      placeholder="10"
                    />
                  </div>
                  <div className="form-field">
                    <label>Min Age</label>
                    <input
                      type="number"
                      name="minAge"
                      value={formData.minAge}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="12"
                    />
                  </div>
                  <div className="form-field">
                    <label>Price per Person</label>
                    <input
                      type="number"
                      name="pricePerPerson"
                      value={formData.pricePerPerson}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="150.00"
                    />
                  </div>
                  <div className="form-field full-width">
                    <label>Languages</label>
                    <div className="array-field">
                      {formData.languages.map((language, index) => (
                        <div key={index} className="array-item">
                          <input
                            type="text"
                            value={language}
                            onChange={(e) =>
                              handleArrayChange(
                                "languages",
                                index,
                                e.target.value
                              )
                            }
                            placeholder="Enter language"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveArrayItem("languages", index)
                            }
                            className="delete-button"
                          >
                            <FaTrash className="icon" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleAddArrayItem("languages", "")}
                        className="add-button"
                      >
                        <FaPlus className="icon" />
                        Add Language
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Key Highlights */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <button
              type="button"
              onClick={() => toggleSection("highlights")}
              className="flex w-full items-center justify-between mb-6"
            >
              <div className="text-left">
                <h2 className="text-lg font-semibold text-gray-900">
                  Key Highlights
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Important features and benefits
                </p>
              </div>
              {expandedSections.highlights ? (
                <FaChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <FaChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {expandedSections.highlights && (
              <div className="space-y-4">
                {formData.keyHighlights.map((highlight, index) => (
                  <div
                    key={index}
                    className="rounded-md border border-gray-200 p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900">
                        Highlight {index + 1}
                      </h4>
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveArrayItem("keyHighlights", index)
                        }
                        className="text-red-600 hover:text-red-800"
                      >
                        <FaTrash className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-900">
                          Title
                        </label>
                        <input
                          type="text"
                          value={highlight.title}
                          onChange={(e) =>
                            handleHighlightChange(
                              index,
                              "title",
                              e.target.value
                            )
                          }
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Enter highlight title"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-900">
                          Description
                        </label>
                        <textarea
                          value={highlight.description}
                          onChange={(e) =>
                            handleHighlightChange(
                              index,
                              "description",
                              e.target.value
                            )
                          }
                          rows={2}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Enter highlight description"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    handleAddArrayItem("keyHighlights", {
                      title: "",
                      description: "",
                    })
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  <FaPlus className="h-4 w-4" />
                  Add Highlight
                </button>
              </div>
            )}
          </div>

          {/* Tour Schedule */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <button
              type="button"
              onClick={() => toggleSection("schedule")}
              className="flex w-full items-center justify-between mb-6"
            >
              <div className="text-left">
                <h2 className="text-lg font-semibold text-gray-900">
                  Tour Schedule
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Day-by-day itinerary and activities
                </p>
              </div>
              {expandedSections.schedule ? (
                <FaChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <FaChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {expandedSections.schedule && (
              <div className="space-y-6">
                {formData.tourSchedule.map((day, dayIndex) => (
                  <div
                    key={dayIndex}
                    className="rounded-md border border-gray-200 p-4"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-900">
                        {day.day}
                      </h4>
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveArrayItem("tourSchedule", dayIndex)
                        }
                        className="text-red-600 hover:text-red-800"
                      >
                        <FaTrash className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-900">
                          Title
                        </label>
                        <input
                          type="text"
                          value={day.title}
                          onChange={(e) =>
                            handleScheduleChange(
                              dayIndex,
                              "title",
                              e.target.value
                            )
                          }
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Day title"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-900">
                          Theme
                        </label>
                        <input
                          type="text"
                          value={day.theme}
                          onChange={(e) =>
                            handleScheduleChange(
                              dayIndex,
                              "theme",
                              e.target.value
                            )
                          }
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Day theme"
                        />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <label className="text-sm font-medium text-gray-900">
                          Timeline
                        </label>
                        <input
                          type="text"
                          value={day.timeline}
                          onChange={(e) =>
                            handleScheduleChange(
                              dayIndex,
                              "timeline",
                              e.target.value
                            )
                          }
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="e.g., 9:00 AM - 5:00 PM"
                        />
                      </div>
                    </div>

                    {/* Activities */}
                    <div className="mb-4">
                      <label className="text-sm font-medium text-gray-900 mb-2 block">
                        Activities
                      </label>
                      <div className="space-y-2">
                        {day.activities.map((activity, activityIndex) => (
                          <div key={activityIndex} className="flex gap-2">
                            <input
                              type="text"
                              value={activity}
                              onChange={(e) =>
                                handleActivityChange(
                                  dayIndex,
                                  activityIndex,
                                  e.target.value
                                )
                              }
                              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Enter activity"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newActivities = day.activities.filter(
                                  (_, i) => i !== activityIndex
                                );
                                handleScheduleChange(
                                  dayIndex,
                                  "activities",
                                  newActivities
                                );
                              }}
                              className="px-3 py-2 text-red-600 hover:text-red-800"
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const newActivities = [...day.activities, ""];
                            handleScheduleChange(
                              dayIndex,
                              "activities",
                              newActivities
                            );
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <FaPlus className="h-4 w-4" />
                          Add Activity
                        </button>
                      </div>
                    </div>

                    {/* Meals */}
                    <div>
                      <label className="text-sm font-medium text-gray-900 mb-2 block">
                        Meals
                      </label>
                      <div className="space-y-2">
                        {day.meals.map((meal, mealIndex) => (
                          <div key={mealIndex} className="flex gap-2">
                            <input
                              type="text"
                              value={meal.time}
                              onChange={(e) =>
                                handleMealChange(
                                  dayIndex,
                                  mealIndex,
                                  "time",
                                  e.target.value
                                )
                              }
                              className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Time"
                            />
                            <input
                              type="text"
                              value={meal.name}
                              onChange={(e) =>
                                handleMealChange(
                                  dayIndex,
                                  mealIndex,
                                  "name",
                                  e.target.value
                                )
                              }
                              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Meal name"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newMeals = day.meals.filter(
                                  (_, i) => i !== mealIndex
                                );
                                handleScheduleChange(
                                  dayIndex,
                                  "meals",
                                  newMeals
                                );
                              }}
                              className="px-3 py-2 text-red-600 hover:text-red-800"
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const newMeals = [
                              ...day.meals,
                              { time: "", name: "" },
                            ];
                            handleScheduleChange(dayIndex, "meals", newMeals);
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <FaPlus className="h-4 w-4" />
                          Add Meal
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    handleAddArrayItem("tourSchedule", {
                      day: `Day ${formData.tourSchedule.length + 1}`,
                      title: "",
                      theme: "",
                      timeline: "",
                      activities: [""],
                      meals: [{ time: "", name: "" }],
                    })
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  <FaPlus className="h-4 w-4" />
                  Add Day
                </button>
              </div>
            )}
          </div>

          {/* Inclusions & Exclusions */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <button
              type="button"
              onClick={() => toggleSection("inclusions")}
              className="flex w-full items-center justify-between mb-6"
            >
              <div className="text-left">
                <h2 className="text-lg font-semibold text-gray-900">
                  Inclusions & Exclusions
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  What's included and what's not
                </p>
              </div>
              {expandedSections.inclusions ? (
                <FaChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <FaChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {expandedSections.inclusions && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-900 mb-3 block">
                    Inclusions
                  </label>
                  <div className="space-y-2">
                    {formData.inclusions.map((inclusion, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={inclusion}
                          onChange={(e) =>
                            handleArrayChange(
                              "inclusions",
                              index,
                              e.target.value
                            )
                          }
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Enter inclusion"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveArrayItem("inclusions", index)
                          }
                          className="px-3 py-2 text-red-600 hover:text-red-800"
                        >
                          <FaTrash className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => handleAddArrayItem("inclusions", "")}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <FaPlus className="h-4 w-4" />
                      Add Inclusion
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-900 mb-3 block">
                    Exclusions
                  </label>
                  <div className="space-y-2">
                    {formData.exclusions.map((exclusion, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={exclusion}
                          onChange={(e) =>
                            handleArrayChange(
                              "exclusions",
                              index,
                              e.target.value
                            )
                          }
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Enter exclusion"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveArrayItem("exclusions", index)
                          }
                          className="px-3 py-2 text-red-600 hover:text-red-800"
                        >
                          <FaTrash className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => handleAddArrayItem("exclusions", "")}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <FaPlus className="h-4 w-4" />
                      Add Exclusion
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Gallery */}
          <div className="form-section">
            <button
              type="button"
              onClick={() => toggleSection("gallery")}
              className="section-header"
            >
              <div className="section-title">
                <h2>Gallery</h2>
                <p>Additional images for the storyteller</p>
              </div>
              {expandedSections.gallery ? (
                <FaChevronUp className="chevron-icon" />
              ) : (
                <FaChevronDown className="chevron-icon" />
              )}
            </button>

            {expandedSections.gallery && (
              <div className="section-content">
                <div className="form-field full-width">
                  <label>Gallery Images</label>
                  <OUpload
                    multiple={true}
                    value={formData.gallery}
                    onImagesUploaded={handleGalleryImageUploaded}
                    onStartLoader={$startLoader}
                    onStopLoader={$stopLoader}
                    text="Upload multiple images for the gallery"
                    size="md"
                    imagesBaseUrl="https://songstarktours.s3.ap-northeast-2.amazonaws.com"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <button
              type="button"
              onClick={() => toggleSection("settings")}
              className="flex w-full items-center justify-between mb-6"
            >
              <div className="text-left">
                <h2 className="text-lg font-semibold text-gray-900">
                  Settings
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Status and visibility options
                </p>
              </div>
              {expandedSections.settings ? (
                <FaChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <FaChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {expandedSections.settings && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    name="sort_order"
                    value={formData.sort_order}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
                <div className="md:col-span-2 space-y-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      name="is_featured"
                      checked={formData.is_featured}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label className="text-sm font-medium text-gray-900">
                      Featured Storyteller
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      name="is_popular"
                      checked={formData.is_popular}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label className="text-sm font-medium text-gray-900">
                      Popular Storyteller
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* SEO Settings */}
          <div className="form-section">
            <button
              type="button"
              onClick={() => toggleSection("seo")}
              className="section-header"
            >
              <div className="section-title">
                <h2>SEO Settings</h2>
                <p>
                  Search engine optimization (auto-generated from basic info)
                </p>
              </div>
              {expandedSections.seo ? (
                <FaChevronUp className="chevron-icon" />
              ) : (
                <FaChevronDown className="chevron-icon" />
              )}
            </button>

            {expandedSections.seo && (
              <div className="section-content">
                <div className="form-grid">
                  <div className="form-field full-width">
                    <label>
                      Meta Title
                      <span className="text-xs text-gray-500 ml-2">
                        (Auto-generated from name, specialty, and location)
                      </span>
                    </label>
                    <input
                      type="text"
                      name="meta_title"
                      value={formData.meta_title}
                      onChange={handleInputChange}
                      maxLength="60"
                      placeholder="SEO title (max 60 characters)"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {formData.meta_title.length}/60 characters
                    </div>
                  </div>
                  <div className="form-field full-width">
                    <label>
                      Meta Description
                      <span className="text-xs text-gray-500 ml-2">
                        (Auto-generated from name, specialty, location, and
                        story)
                      </span>
                    </label>
                    <textarea
                      name="meta_description"
                      value={formData.meta_description}
                      onChange={handleInputChange}
                      maxLength="160"
                      rows={3}
                      placeholder="SEO description (max 160 characters)"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {formData.meta_description.length}/160 characters
                    </div>
                  </div>
                  <div className="form-field full-width">
                    <label>
                      Meta Keywords
                      <span className="text-xs text-gray-500 ml-2">
                        (Auto-generated from specialty and location)
                      </span>
                    </label>
                    <input
                      type="text"
                      name="meta_keywords"
                      value={formData.meta_keywords}
                      onChange={handleInputChange}
                      placeholder="keyword1, keyword2, keyword3"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={() => navigate("/storytellers")}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <FaSave className="h-4 w-4" />
              )}
              {isEdit ? "Update Storyteller" : "Create Storyteller"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StorytellerForm;
